import _ from "lodash";

/**
 * Initializes WPD PlotData class.
 */
function init(wpd, dataEntryVM) {
	wpd.PlotData.prototype.addDataset = function (ds) {
		this._datasetColl.push(ds);

		// by default bind ds to last non-table axes to prevent assignment of non-table datasets to table axes
		const axes = this._axesColl.filter((axis) => !axis.getMetadata().table);
		const axCount = axes.length;
		if (axCount > 0) {
			let axis = axes[axCount - 1];
			this.setAxesForDataset(ds, axis);
		}
	};

	wpd.PlotData.prototype.getDatasetsForAxis = function (axis) {
		const datasets = [];

		this._objectAxesMap.forEach((value, key) => {
			if (key instanceof wpd.Dataset && value === axis) {
				datasets.push(key);
			}
		});

		return datasets;
	};

	wpd.PlotData.prototype.getDatasetDisplayNames = function () {
		return this._datasetColl.map((ds) => {
			const metadata = ds.getMetadata();
			return metadata.name || ds.name;
		});
	};

	if (dataEntryVM.pointGroupOverhaul) {
		// load
		wpd.PlotData.prototype.deserialize = function (data) {
			this.reset();

			// collect page data if it exists
			let documentMetadata = {};

			if (!data) {
				return documentMetadata;
			}

			const collectMetadata = (group, type, key, object) => {
				if (!documentMetadata[group]) documentMetadata[group] = {};
				if (!documentMetadata[group][type]) documentMetadata[group][type] = {};
				if (!documentMetadata[group][type][key])
					documentMetadata[group][type][key] = [];
				documentMetadata[group][type][key].push(object);
			};

			// axes data
			if (data.axesColl != null) {
				for (let axIdx = 0; axIdx < data.axesColl.length; axIdx++) {
					const axData = data.axesColl[axIdx];

					// get calibration
					let calibration = null;
					if (axData.type !== "ImageAxes") {
						if (axData.type === "TernaryAxes") {
							calibration = new wpd.Calibration(3);
						} else {
							calibration = new wpd.Calibration(2);
						}
						for (
							let calIdx = 0;
							calIdx < axData.calibrationPoints.length;
							calIdx++
						) {
							calibration.addPoint(
								axData.calibrationPoints[calIdx].px,
								axData.calibrationPoints[calIdx].py,
								axData.calibrationPoints[calIdx].dx,
								axData.calibrationPoints[calIdx].dy,
								axData.calibrationPoints[calIdx].dz,
							);
						}
					}

					// create axes
					let axes = null;
					if (axData.type === "XYAxes") {
						axes = new wpd.XYAxes();
						calibration.labels = ["X1", "X2", "Y1", "Y2"];
						calibration.labelPositions = ["N", "N", "E", "E"];
						calibration.maxPointCount = 4;
						axes.calibrate(
							calibration,
							axData.isLogX,
							axData.isLogY,
							axData.noRotation,
						);
					} else if (axData.type === "BarAxes") {
						axes = new wpd.BarAxes();
						calibration.labels = ["P1", "P2"];
						calibration.labelPositions = ["S", "S"];
						calibration.maxPointCount = 2;
						axes.calibrate(
							calibration,
							axData.isLog,
							axData.isRotated == null ? false : axData.isRotated,
						);
					} else if (axData.type === "PolarAxes") {
						axes = new wpd.PolarAxes();
						calibration.labels = ["Origin", "P1", "P2"];
						calibration.labelPositions = ["E", "S", "S"];
						calibration.maxPointCount = 3;
						axes.calibrate(
							calibration,
							axData.isDegrees,
							axData.isClockwise,
							axData.isLog,
						);
					} else if (axData.type === "TernaryAxes") {
						axes = new wpd.TernaryAxes();
						calibration.labels = ["A", "B", "C"];
						calibration.labelPositions = ["S", "S", "E"];
						calibration.maxPointCount = 3;
						axes.calibrate(
							calibration,
							axData.isRange100,
							axData.isNormalOrientation,
						);
					} else if (axData.type === "MapAxes") {
						axes = new wpd.MapAxes();
						calibration.labels = ["P1", "P2"];
						calibration.labelPositions = ["S", "S"];
						calibration.maxPointCount = 2;
						let originLocation =
							axData.originLocation != null
								? axData.originLocation
								: "top-left";
						let imageHeight =
							axData.imageHeight != null ? parseInt(axData.imageHeight, 10) : 0;
						axes.calibrate(
							calibration,
							axData.scaleLength,
							axData.unitString,
							originLocation,
							imageHeight,
						);
					} else if (axData.type === "ImageAxes") {
						axes = new wpd.ImageAxes();
					} else if (axData.type === "CircularChartRecorderAxes") {
						axes = new wpd.CircularChartRecorderAxes();
						calibration.labels = [
							"(T0,R0)",
							"(T0,R1)",
							"(T0,R2)",
							"(T1,R2)",
							"(T2,R2)",
						];
						calibration.labelPositions = ["S", "S", "S", "S", "S"];
						calibration.maxPointCount = 5;
						axes.calibrate(calibration, axData.startTime);
					}

					if (axes != null) {
						axes.name = axData.name;

						if (axData.metadata !== undefined) {
							axes.setMetadata(axData.metadata);
						}

						this._axesColl.push(axes);

						// collect document metadata
						if (axData.file !== undefined) {
							collectMetadata("file", "axes", axData.file, axes);
						}
						if (axData.page !== undefined) {
							collectMetadata("page", "axes", axData.page, axes);
						}
					}
				}
			}

			// datasets
			if (data.datasetColl != null) {
				for (let dsIdx = 0; dsIdx < data.datasetColl.length; dsIdx++) {
					const dsData = data.datasetColl[dsIdx];
					let ds = new wpd.Dataset();
					ds.name = dsData.name;

					// set axis for this dataset
					const axisIndex = this.getAxesNames().indexOf(dsData.axesName);
					const axis = this._axesColl[axisIndex];

					this.setAxesForDataset(ds, axis);

					if (dsData.colorRGB != null) {
						ds.colorRGB = new wpd.Color(
							dsData.colorRGB[0],
							dsData.colorRGB[1],
							dsData.colorRGB[2],
						);
					}

					// data point groups for backwards compatibility for
					// pre-axis point groups data
					let convertedMainTagUuids;
					if (
						dsData.groupNames !== undefined &&
						(!dsData.metadata?.pointGroups || !axis.getMetadata().pointGroups)
					) {
						convertedMainTagUuids = wpd.pointGroups.updateToNewFormat(
							axis,
							null,
							dsData.groupNames,
						);

						ds.setPointGroups(convertedMainTagUuids);
					}

					// dataset metadata
					if (dsData.metadata !== undefined) {
						ds.setMetadata(dsData.metadata);
						// setMetadata replaces the dataset metadata wholesale, which
						// clobbers the pointGroups the legacy conversion just set above
						// (legacy on-disk metadata carries none). Re-apply them so the
						// migration survives into the tuple-regen loop and the renderer
						// instead of leaving the dataset point-group-less. (DCTL-1767)
						if (convertedMainTagUuids) {
							ds.setPointGroups(convertedMainTagUuids);
						}
						// update dataset point groups to newer format that includes both x and y center tags
						if (
							dsData.metadata.pointGroups &&
							dsData.metadata.pointGroups.some(
								(pointGroup) => pointGroup.axis === "center",
							)
						) {
							ds.setPointGroups(wpd.pointGroups.getCenterTagUuids(axis));
						}
					}
					// data points metadata keys
					if (dsData.metadataKeys != null) {
						ds.setMetadataKeys(dsData.metadataKeys);
					}

					// data points
					for (let pxIdx = 0; pxIdx < dsData.data.length; pxIdx++) {
						// for backwards compatibility; metadata was updated from array
						// to object
						let metadata = dsData.data[pxIdx].metadata;
						if (dsData.data[pxIdx].metadata != null) {
							if (Array.isArray(metadata)) {
								// transform metadata array into object
								metadata = metadata.reduce((obj, val, idx) => {
									return {
										...obj,
										[dsData.metadataKeys[idx]]: val,
									};
								}, {});
							}
						}

						ds.addPixel(dsData.data[pxIdx].x, dsData.data[pxIdx].y, metadata);
					}

					// add tuple info to data point metadata if it doesn't exist
					const axisMetadata = axis.getMetadata();
					if (
						axisMetadata.axesLabelData &&
						axisMetadata.pointGroups &&
						ds.getMetadata().pointGroups
					) {
						for (const dataPoint of ds.getAllPixels()) {
							if (_.isEmpty(dataPoint.metadata.tuple)) {
								// update tagGroupAxisDirection to pointGroupAxisDirection
								delete dataPoint.metadata.tagGroupAxisDirection;

								Object.keys(axisMetadata.pointGroups).forEach((direction) => {
									const axisDirection = wpd.pointGroups.isCenterDataPoint(
										axis,
										dataPoint,
									)
										? "center"
										: direction;

									const tupleData = wpd.pointGroups.generateTupleData(
										ds,
										dataPoint,
										axisDirection,
									);

									// Only attach a tuple whose every side carries an id. A side
									// without one is rejected by the server on save, and since this
									// runs on every load the record would be stuck unsaveable.
									// generateTupleData no longer produces such a tuple; this is
									// defence in depth at the one write site that can create the
									// loop. (DCTL-2561)
									if (
										tupleData &&
										Object.values(tupleData).every((datum) => datum?.id)
									) {
										dataPoint.metadata.tuple = tupleData;
										dataPoint.metadata.pointGroupAxisDirection = axisDirection;
									}
								});
							}
						}
					}

					this._datasetColl.push(ds);

					// collect document metadata
					if (dsData.file !== undefined) {
						collectMetadata("file", "datasets", dsData.file, ds);
					}
					if (dsData.page !== undefined) {
						collectMetadata("page", "datasets", dsData.page, ds);
					}
				}
			}

			// misc
			if (data.misc != null) {
				documentMetadata.misc = data.misc;
			}

			return documentMetadata;
		};

		// save
		wpd.PlotData.prototype.serialize = function (documentMetadata) {
			let data = {};
			data.version = [4, 2];
			data.axesColl = [];
			data.datasetColl = [];
			data.measurementColl = [];

			// axes data
			for (let axIdx = 0; axIdx < this._axesColl.length; axIdx++) {
				const axes = this._axesColl[axIdx];
				let axData = {};
				axData.name = axes.name;

				// file and page metadata
				if (documentMetadata) {
					if (
						documentMetadata.file &&
						documentMetadata.file.axes[axes.name] !== undefined
					) {
						axData.file = documentMetadata.file.axes[axes.name];
					}
					if (
						documentMetadata.page &&
						documentMetadata.page.axes[axes.name] !== undefined
					) {
						axData.page = documentMetadata.page.axes[axes.name];
					}
				}

				// axes data
				if (axes instanceof wpd.XYAxes) {
					axData.type = "XYAxes";
					axData.isLogX = axes.isLogX();
					axData.isLogY = axes.isLogY();
					axData.noRotation = axes.noRotation();
				} else if (axes instanceof wpd.BarAxes) {
					axData.type = "BarAxes";
					axData.isLog = axes.isLog();
					axData.isRotated = axes.isRotated();
				} else if (axes instanceof wpd.PolarAxes) {
					axData.type = "PolarAxes";
					axData.isDegrees = axes.isThetaDegrees();
					axData.isClockwise = axes.isThetaClockwise();
					axData.isLog = axes.isRadialLog();
				} else if (axes instanceof wpd.TernaryAxes) {
					axData.type = "TernaryAxes";
					axData.isRange100 = axes.isRange100();
					axData.isNormalOrientation = axes.isNormalOrientation;
				} else if (axes instanceof wpd.MapAxes) {
					axData.type = "MapAxes";
					axData.scaleLength = axes.getScaleLength();
					axData.unitString = axes.getUnits();
					axData.originLocation = axes.getOriginLocation();
					axData.imageHeight = axes.getImageHeight();
				} else if (axes instanceof wpd.ImageAxes) {
					axData.type = "ImageAxes";
				} else if (axes instanceof wpd.CircularChartRecorderAxes) {
					axData.type = "CircularChartRecorderAxes";
					axData.startTime = axes.getStartTime();
				}

				// include axes metadata, if present
				if (Object.keys(axes.getMetadata()).length > 0) {
					axData.metadata = axes.getMetadata();
				}

				// calibration points
				if (!(axes instanceof wpd.ImageAxes)) {
					axData.calibrationPoints = [];
					for (let calIdx = 0; calIdx < axes.calibration.getCount(); calIdx++) {
						axData.calibrationPoints.push(axes.calibration.getPoint(calIdx));
					}
				}

				data.axesColl.push(axData);
			}

			// datasets
			for (let dsIdx = 0; dsIdx < this._datasetColl.length; dsIdx++) {
				const ds = this._datasetColl[dsIdx];
				const axes = this.getAxesForDataset(ds);

				// dataset information
				let dsData = {};
				dsData.name = ds.name;
				dsData.axesName = axes != null ? axes.name : "";
				dsData.colorRGB = ds.colorRGB.serialize();
				dsData.metadataKeys = ds.getMetadataKeys(); // point metadata keys
				// include file and page information, if present
				if (documentMetadata) {
					if (
						documentMetadata.file &&
						documentMetadata.file.datasets[ds.name] !== undefined
					) {
						dsData.file = documentMetadata.file.datasets[ds.name];
					}
					if (
						documentMetadata.page &&
						documentMetadata.page.datasets[ds.name] !== undefined
					) {
						dsData.page = documentMetadata.page.datasets[ds.name];
					}
				}

				// include dataset metadata, if present
				if (Object.keys(ds.getMetadata()).length > 0) {
					// this is metadata on the dataset itself, not to be confused with metadataKeys which denote metadata keys on
					// each data point within the dataset
					dsData.metadata = ds.getMetadata();
				}

				// data points
				dsData.data = [];
				for (let pxIdx = 0; pxIdx < ds.getCount(); pxIdx++) {
					let px = ds.getPixel(pxIdx);

					dsData.data[pxIdx] = px;
					if (axes != null) {
						dsData.data[pxIdx].value = axes.pixelToData(px.x, px.y);
					}
				}

				data.datasetColl.push(dsData);
			}

			if (documentMetadata && documentMetadata.misc) {
				data.misc = documentMetadata.misc;
			}

			return data;
		};
	}
}

export { init };
